import { supabase } from "../../supabaseClient";
import logger from "../../utils/logger";
import { withTimeout } from "../../utils/withTimeout";

const ROLE_PRIORITY = {
  megaadmin: 1,
  superadmin: 2,
  admin: 3,
  assistant: 4,
  tutor: 5,
  student: 6,
};

const normalizeClasses = (classes) => {
  if (Array.isArray(classes)) return classes.filter(Boolean);
  if (classes === null || classes === undefined || classes === "") return [];
  return [classes].filter(Boolean);
};

const getRolePriority = (role) => ROLE_PRIORITY[String(role || "").toLowerCase()] || 99;

export const normalizeCurrentUserProfile = (profile = {}) => {
  const role =
    typeof profile.role === "string" && profile.role.trim()
      ? profile.role.trim().toLowerCase()
      : "student";

  return {
    id: profile.id || profile.auth_user_id || null,
    auth_user_id: profile.auth_user_id || profile.id || null,
    email: profile.email || null,
    full_name: profile.full_name || profile.name || null,
    role,
    class: profile.class || null,
    classes: normalizeClasses(profile.classes),
    is_active: profile.is_active ?? true,
    updated_at: profile.updated_at || null,
  };
};

export const pickCanonicalCurrentUserProfile = (profiles) => {
  if (!Array.isArray(profiles) || profiles.length === 0) {
    return null;
  }

  return [...profiles]
    .map((profile) => normalizeCurrentUserProfile(profile))
    .sort((left, right) => {
      const roleDiff = getRolePriority(left.role) - getRolePriority(right.role);
      if (roleDiff !== 0) return roleDiff;

      const leftUpdatedAt = left.updated_at
        ? new Date(left.updated_at).getTime()
        : 0;
      const rightUpdatedAt = right.updated_at
        ? new Date(right.updated_at).getTime()
        : 0;

      return rightUpdatedAt - leftUpdatedAt;
    })[0];
};

export const getCurrentUserProfile = async () => {
  try {
    // Обёрнуто в withTimeout: RPC сам по себе быстрый (~6мс), но при потере/зависании
    // сетевого запроса (мобильный интернет) вызов висел и держал спиннер входа до
    // safety-таймаута. Таймаут → status "error" → вызывающий код отдаёт fallback-профиль
    // (пользователь остаётся залогинен, роль догружается позже), а не бесконечная загрузка.
    // Адаптивный таймаут: на медленных сетях (3g/slow-2g) 8с мало —
    // rtt до 2.6с означает 3-5 сетевых раундов только на установку соединения.
    const isSlowNetwork = (() => {
      try {
        if (typeof navigator !== "undefined" && navigator.connection) {
          const et = (navigator.connection.effectiveType || "").toLowerCase();
          return et === "slow-2g" || et === "2g" || (et === "3g" && (navigator.connection.rtt || 0) >= 1500);
        }
      } catch (e) {
        // navigator.connection недоступен — таймаут по умолчанию
      }
      return false;
    })();
    const { data, error } = await withTimeout(
      supabase.rpc("get_current_user_profile"),
      isSlowNetwork ? 20000 : 8000,
      "get_current_user_profile timeout",
    );

    if (error) {
      const isTimeoutErr = /timeout/i.test(error?.message || "");
      const logFn = isTimeoutErr ? logger.warn : logger.error;
      logFn("getCurrentUserProfile RPC error", error);
      return {
        success: false,
        status: "error",
        profile: null,
        error,
      };
    }

    const rawProfile = Array.isArray(data)
      ? pickCanonicalCurrentUserProfile(data)
      : data;

    if (!rawProfile) {
      return {
        success: false,
        status: "missing_profile",
        profile: null,
        error: null,
      };
    }

    const profile = normalizeCurrentUserProfile(rawProfile);

    if (profile.is_active === false) {
      return {
        success: false,
        status: "inactive_profile",
        profile,
        error: null,
      };
    }

    return {
      success: true,
      status: "ok",
      profile,
      error: null,
    };
  } catch (error) {
    logger.error("getCurrentUserProfile unexpected error", error);
    return {
      success: false,
      status: "error",
      profile: null,
      error,
    };
  }
};

export default {
  getCurrentUserProfile,
  normalizeCurrentUserProfile,
};
